import { useState, useEffect } from 'react';
import { Search } from 'lucide-react';

interface User {
  id: string;
  email: string;
  username: string;
  phone?: string;
  role: 'admin' | 'user' | 'responsable' | 'pasteur';
}

interface UserSelectProps {
  value: string;
  onChange: (value: string) => void;
  users: User[];
}

export function UserSelect({ value, onChange, users }: UserSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    const user = users.find(u => u.email === value);
    if (user) {
      setSelectedUser(user);
    }
  }, [value, users]);

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const cleanedSearch = searchTerm.replace(/\s/g, '');

    return (
      user.username.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      (user.phone && user.phone.replace(/\s/g, '').includes(cleanedSearch))
    );
  });

  const handleSelect = (user: User) => {
    onChange(user.email);
    setSelectedUser(user);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
      >
        {selectedUser ? (
          <span>{selectedUser.username}</span>
        ) : (
          <span className="text-gray-500">Sélectionner un utilisateur</span>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher par nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => handleSelect(user)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50"
              >
                <div className="font-medium">{user.username}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
